/**
 * Collapsible — smooth height-animated expand/collapse, web & native.
 *
 * Reanimated layout animations have spotty web support for height
 * interpolation. This primitive sidesteps that by measuring the
 * content with `onLayout` and animating the wrapper's `height`
 * directly with `withSpring`. Same shared-value drives `opacity`,
 * so the content fades while the container resizes — the Apple
 * "unfold" feel.
 *
 * Usage:
 *
 *   <Collapsible expanded={showLifestyle}>
 *     {...rows}
 *   </Collapsible>
 *
 * Content is always mounted (we need it laid out to measure it).
 * When collapsed, height clips to 0 and opacity fades to 0 — content
 * remains in the tree but is visually + interactively hidden.
 */
import { useEffect, useState, type ReactNode } from "react";
import { View, type LayoutChangeEvent, type ViewStyle } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
} from "react-native-reanimated";

type Props = {
  expanded: boolean;
  children: ReactNode;
  /** Optional spring config override. Defaults tuned to match iOS
   *  expand/collapse feel: gentle overshoot, ~280ms perceived. */
  springConfig?: { damping?: number; stiffness?: number; mass?: number };
  style?: ViewStyle;
};

const DEFAULT_SPRING = { damping: 22, stiffness: 180, mass: 0.8 };

export function Collapsible({
  expanded,
  children,
  springConfig,
  style,
}: Props) {
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const heightProgress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    heightProgress.value = withSpring(expanded ? 1 : 0, {
      ...DEFAULT_SPRING,
      ...springConfig,
    });
  }, [expanded, heightProgress, springConfig]);

  const animatedStyle = useAnimatedStyle(() => {
    // Until we've measured the content once, render at its natural
    // height when expanded (no animation on first show) and 0 when
    // collapsed.
    if (contentHeight == null) {
      return { opacity: expanded ? 1 : 0 };
    }
    return {
      height: heightProgress.value * contentHeight,
      opacity: heightProgress.value,
    };
  }, [contentHeight, expanded]);

  const onContentLayout = (e: LayoutChangeEvent) => {
    const h = e.nativeEvent.layout.height;
    if (h > 0 && h !== contentHeight) {
      setContentHeight(h);
    }
  };

  return (
    <Animated.View
      style={[{ overflow: "hidden" }, animatedStyle, style]}
      pointerEvents={expanded ? "auto" : "none"}
    >
      <View onLayout={onContentLayout}>{children}</View>
    </Animated.View>
  );
}
